import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sliders, Cpu, Eye, X, Link as LinkIcon, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../../../api/client';
import { Modal } from '../../../../../components/ui/Modal';
import type { Perfil, Topico, Subtopico, LockedSpotlight } from '../../types';

// Existing hooks
import { useEcadZoomPan } from './hooks/useEcadZoomPan';
import { useEcadSnapping } from './hooks/useEcadSnapping';
import { useEcadMediaRecorder } from './hooks/useEcadMediaRecorder';
import { useRecorderHealth } from './hooks/useRecorderHealth';

// Extracted hooks (Fase 2)
import { useCatalogCrud } from './hooks/useCatalogCrud';
import { useSegmentPlayback } from './hooks/useSegmentPlayback';
import { useTtsPreview } from './hooks/useTtsPreview';
import { useSegmentAudio } from './hooks/useSegmentAudio';

// Extracted utilities (Fase 1)
import { getElementMetadata } from './utils/getElementMetadata';
import { RECORDER_STYLES } from './utils/recorderStyles';

// Existing components
import { ControlToolbar } from './components/ControlToolbar';
import { SpotlightOverlay } from './components/SpotlightOverlay';
import { KeypressVisualizer } from './components/KeypressVisualizer';
import { RecordingFAB } from './components/RecordingFAB';

// Extracted tab components (Fase 3)
import { StudioTab } from './components/StudioTab';
import { RenderTab } from './components/RenderTab';
import { PreviewTab } from './components/PreviewTab';
import { CatalogTab } from './components/CatalogTab';
import { EmergencyOverlay } from './components/EmergencyOverlay';
import { IframeNavBar } from './components/IframeNavBar';

interface RenderVersion {
  version: number;
  videoUrl: string;
  vttUrl: string;
  audioMode: string;
  voiceId: string;
  createdAt: string;
}


export function EcadRecorderPageModular() {
  const navigate = useNavigate();

  const handleExit = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const targetUrl = params.get('url') || '/master/dashboard';
    navigate(targetUrl);
  }, [navigate]);

  // Preserved master token
  const [masterToken] = useState(() => sessionStorage.getItem('ac_token') || localStorage.getItem('ac_token') || '');

  // Catalog and linking states
  const [catalog, setCatalog] = useState<Perfil[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [selectedPerfilId, setSelectedPerfilId] = useState('');
  const [selectedTopicoId, setSelectedTopicoId] = useState('');
  const [selectedSubtopicoId, setSelectedSubtopicoId] = useState('');
  const [forceRecreate, setForceRecreate] = useState(false);
  const [activeTab, setActiveTab] = useState<'studio' | 'render' | 'preview' | 'catalog'>('studio');
  const [isFeedbackMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'feedback';
  });
  const [isKanbanRelayMode] = useState(() => {
    return !!new URLSearchParams(window.location.search).get('kanban_relay');
  });
  const [showFeedbackModalInRecorder, setShowFeedbackModalInRecorder] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    // Kanban Relay mode: nunca mostrar feedback modal — relay é automático
    if (params.get('kanban_relay')) return false;
    if (params.get('mode') !== 'feedback') return false;
    try {
      const saved = sessionStorage.getItem('ecad_session');
      if (saved) {
        const session = JSON.parse(saved);
        if (session.timestamp && Date.now() - session.timestamp <= 2 * 60 * 60 * 1000) {
          return true;
        }
      }
    } catch (e) {}
    return false;
  });
  const [feedbackTitle, setFeedbackTitle] = useState('');
  const [feedbackDesc, setFeedbackDesc] = useState('');
  const [iaGenerating, setIaGenerating] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [publishingRemote, setPublishingRemote] = useState(false);

  const [showOnboarding, setShowOnboarding] = useState(() => {
    // Kanban Relay mode: nunca mostrar onboarding — fluxo deve ser limpo e direto
    if (new URLSearchParams(window.location.search).get('kanban_relay')) return false;
    return localStorage.getItem('ecad_hide_onboarding') !== 'true';
  });
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [dontShowOnboardingAgain, setDontShowOnboardingAgain] = useState(true);

  const [isLinkingModalOpenRaw, setIsLinkingModalOpenRaw] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'feedback') return false;
    try {
      const saved = sessionStorage.getItem('ecad_session');
      if (saved) {
        const session = JSON.parse(saved);
        if (session.timestamp && Date.now() - session.timestamp <= 2 * 60 * 60 * 1000) {
          return true;
        }
      }
    } catch (e) {}
    return false;
  });
  const isLinkingModalOpen = isLinkingModalOpenRaw && !isFeedbackMode;
  const setIsLinkingModalOpen = useCallback((val: boolean) => {
    if (isFeedbackMode && val) return;
    setIsLinkingModalOpenRaw(val);
  }, [isFeedbackMode]);
  const [showManualInput, setShowManualInput] = useState(false);

  // Modal navigation configurations
  const [iframeUrl] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('url') || '/';
  });
  const [addressBarUrl, setAddressBarUrl] = useState(iframeUrl);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const [forceLogoutOnActivation, setForceLogoutOnActivation] = useState(true);
  const configMenuRef = useRef<HTMLDivElement>(null);

  // Cursor & Spotlight Interaction States
  // 🔴 PERF: cursorPos is now ref-only — NO useState to avoid 60fps re-renders
  const cursorPosRef = useRef({ x: 0, y: 0 });
  const cursorElRef = useRef<HTMLDivElement | null>(null); // direct DOM ref for cursor SVG
  const [showCursor, setShowCursor] = useState(false);
  const showCursorRef = useRef(false);
  const [clickRipples, setClickRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const [spotlightRadius, setSpotlightRadius] = useState(() => {
    const saved = localStorage.getItem('ecad_spotlight_radius');
    return saved ? Math.max(30, Math.min(300, Number(saved))) : 80;
  });
  const [spotlightShape, setSpotlightShape] = useState<'circle' | 'square'>(() => {
    const saved = localStorage.getItem('ecad_spotlight_shape');
    return (saved === 'circle' || saved === 'square') ? saved : 'circle';
  });
  const [customSpotlight, setCustomSpotlight] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const rightStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const isRightDraggingRef = useRef(false);

  const [lockedSpotlights, setLockedSpotlights] = useState<LockedSpotlight[]>([]);
  const lockedSpotlightsRef = useRef(lockedSpotlights);
  useEffect(() => { lockedSpotlightsRef.current = lockedSpotlights; }, [lockedSpotlights]);

  const [hoveredArrowIndex, setHoveredArrowIndex] = useState<number | null>(null);
  const clickHandledByArrowRef = useRef(false);
  const [draggedSpotlightId, setDraggedSpotlightId] = useState<string | null>(null);
  const draggedSpotlightIdRef = useRef<string | null>(null);
  useEffect(() => { draggedSpotlightIdRef.current = draggedSpotlightId; }, [draggedSpotlightId]);
  
  const lastCursorPosRef = useRef({ x: 0, y: 0 });
  const maybeDraggingSpotlightIdRef = useRef<string | null>(null);
  const dragStartCursorPosRef = useRef({ x: 0, y: 0 });

  const [hoveredSpotlightId, setHoveredSpotlightId] = useState<string | null>(null);
  const hoveredSpotlightIdRef = useRef<string | null>(null);
  useEffect(() => { hoveredSpotlightIdRef.current = hoveredSpotlightId; }, [hoveredSpotlightId]);

  const [deletePendingSpotlightId, setDeletePendingSpotlightId] = useState<string | null>(null);
  const deletePendingSpotlightIdRef = useRef<string | null>(null);
  useEffect(() => { deletePendingSpotlightIdRef.current = deletePendingSpotlightId; }, [deletePendingSpotlightId]);

  // Keystrokes state
  const [keystrokesEnabled, setKeystrokesEnabled] = useState(false);
  const [lastKeys, setLastKeys] = useState('');
  const [showKeys, setShowKeys] = useState(false);

  // Video metadata
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');

  // Editing segments inside transcript modal
  const [editingSegmentIndex, setEditingSegmentIndex] = useState<number | null>(null);
  const [editingSegmentText, setEditingSegmentText] = useState('');

  // Studio render states
  const [isRendering, setIsRendering] = useState(false);
  const [renderedVersions, setRenderedVersions] = useState<RenderVersion[]>([]);
  const [activeRenderIndex, setActiveRenderIndex] = useState(-1);

  // Spotlight active toggle state
  const [isSpotlightActive, setIsSpotlightActive] = useState(false);
  const isSpotlightActiveRef = useRef(isSpotlightActive);

  // Key press behavior state machine refs for tap-to-toggle/hold-to-activate zoom/spotlight
  const shiftPressTimeRef = useRef<number>(0);
  const zoomModeRef = useRef<'off' | 'toggled' | 'holding'>('off');
  const ctrlPressTimeRef = useRef<number>(0);
  const spotlightModeRef = useRef<'off' | 'toggled' | 'holding'>('off');

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Voice & model states
  const [selectedModel, setSelectedModel] = useState(() => localStorage.getItem('ecad_last_model') || 'gemini-2.5-pro');

  useEffect(() => {
    localStorage.setItem('ecad_last_model', selectedModel);
  }, [selectedModel]);

  // Initialize health monitor FIRST — other hooks depend on it
  const health = useRecorderHealth();

  // Initialize existing hooks (health passed to hooks that track resources)
  const snapping = useEcadSnapping();
  const recorder = useEcadMediaRecorder(masterToken);
  const zoomPan = useEcadZoomPan(cursorPosRef, recorder.status, health);
  const ttsPreview = useTtsPreview();
  // Stable ref-based callback for ttsGetVoice — prevents useCallback cascade in usePreviewPlayer
  const ttsSelectedVoiceIdRef = useRef(ttsPreview.selectedVoiceId);
  ttsSelectedVoiceIdRef.current = ttsPreview.selectedVoiceId;
  const stableTtsGetVoice = useCallback(() => {
    const available = window.speechSynthesis?.getVoices() || [];
    return available.find(v => v.voiceURI === ttsSelectedVoiceIdRef.current) || null;
  }, []);
  const segmentAudio = useSegmentAudio();

  // ══════════════════════════════════════════════════════
  // ── Recording Mode Master Switch ──
  // When false, ALL recording overlays/listeners are dormant.
  // ══════════════════════════════════════════════════════
  const isRecordingModeRef = useRef(false);

  const deactivateRecordingMode = useCallback(() => {
    console.log('[ECAD] 🔴 deactivateRecordingMode — desligando tudo do modo gravação');
    isRecordingModeRef.current = false;

    // Cursor fake
    showCursorRef.current = false;
    if (cursorElRef.current) cursorElRef.current.style.display = 'none';

    // Spotlight
    setIsSpotlightActive(false);
    spotlightModeRef.current = 'off';
    setCustomSpotlight(null);
    setLockedSpotlights([]);
    setHoveredSpotlightId(null);
    setDeletePendingSpotlightId(null);

    // Zoom
    zoomPan.setIsZoomed(false);
    zoomModeRef.current = 'off';

    // Click ripples
    setClickRipples([]);

    // Keystroke visualizer
    setShowKeys(false);

    // Snapping
    setSnappedRect(null);
    setAutoSnapEnabled(true);

    // Drag refs
    rightStartPosRef.current = null;
    isRightDraggingRef.current = false;
    maybeDraggingSpotlightIdRef.current = null;
    setDraggedSpotlightId(null);

    // Remove CSS classes from body + iframe
    updateCursorVisibility(false);
  }, []);

  const activateRecordingMode = useCallback(() => {
    console.log('[ECAD] 🟢 activateRecordingMode — ativando modo gravação');
    isRecordingModeRef.current = true;
    updateCursorVisibility(true);
  }, []);

  // Auto-deactivate when leaving recording/paused — drives both master switch AND health phase
  useEffect(() => {
    const isActive = recorder.status === 'recording' || recorder.status === 'paused';
    if (isActive && !isRecordingModeRef.current) {
      // First activation
      activateRecordingMode();
      if (recorder.status === 'recording') {
        health.transitionTo('RECORDING');
      } else if (recorder.status === 'paused') {
        health.transitionTo('PAUSED');
      }
    } else if (isActive && isRecordingModeRef.current) {
      // Already active — sync health phase (handles recording↔paused transitions)
      if (recorder.status === 'recording' && health.phaseRef.current !== 'RECORDING') {
        health.transitionTo('RECORDING');
      } else if (recorder.status === 'paused' && health.phaseRef.current !== 'PAUSED') {
        health.transitionTo('PAUSED');
      }
    } else if (!isActive && isRecordingModeRef.current) {
      // Stopping → cleanup via health transition
      // NOTE: useEcadMediaRecorder emits 'completed' (not 'stopped') after recording ends
      if (recorder.status === 'completed' || recorder.status === 'idle') {
        const hasVideo = !!recorder.previewUrl;
        health.transitionTo('STOPPING', () => {
          deactivateRecordingMode();
          // Defer second transition to let STOPPING cleanup finish first
          queueMicrotask(() => {
            if (hasVideo) {
              health.transitionTo('EDITING');
            } else {
              health.transitionTo('IDLE');
            }
          });
        });
      }
    }
  }, [recorder.status]);

  // Safety: ensure ac-recording-active class is always removed on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('ac-recording-active');
    };
  }, []);

  const hasTranscription = !!(recorder.videoScript && recorder.videoScript.trim()) || showManualInput;

  useEffect(() => {
    if (!hasTranscription && (activeTab === 'render' || activeTab === 'preview')) {
      setActiveTab('studio');
    }
  }, [hasTranscription, activeTab]);

  // Destructure snapping references
  const {
    autoSnapEnabled, setAutoSnapEnabled,
    snappedRect, setSnappedRect,
    lastSnappedElementRef, iframeObstaclesRef,
    getControlAncestor, getCleanSelector
  } = snapping;

  // Destructure zoom references
  const {
    zoomEnabled, setZoomEnabled,
    isZoomed, setIsZoomed,
    zoomScale, setZoomScale,
    zoomTxRef, zoomTyRef,
    zoomTransitioning, panTrigger
  } = zoomPan;

  // Synced refs for state values
  const isZoomedRef = useRef(isZoomed);
  const zoomEnabledRef = useRef(zoomEnabled);
  const spotlightEnabledRef = useRef(recorder.useMic);
  const zoomScaleRef = useRef(zoomScale);
  const statusRef = useRef(recorder.status);
  const autoSnapEnabledRef = useRef(autoSnapEnabled);

  useEffect(() => { isZoomedRef.current = isZoomed; }, [isZoomed]);
  useEffect(() => { zoomEnabledRef.current = zoomEnabled; }, [zoomEnabled]);
  useEffect(() => { zoomScaleRef.current = zoomScale; }, [zoomScale]);
  useEffect(() => { statusRef.current = recorder.status; }, [recorder.status]);
  useEffect(() => { autoSnapEnabledRef.current = autoSnapEnabled; }, [autoSnapEnabled]);
  useEffect(() => {
    isSpotlightActiveRef.current = isSpotlightActive;
    const classNameSpotlight = 'ac-spotlight-active';
    if (!isSpotlightActive) {
      setCustomSpotlight(null);
      rightStartPosRef.current = null;
      isRightDraggingRef.current = false;
      setLockedSpotlights([]);
      document.body.classList.remove(classNameSpotlight);
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
          iframe.contentDocument.body.classList.remove(classNameSpotlight);
        }
      } catch (e) {}
    } else {
      document.body.classList.add(classNameSpotlight);
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
          iframe.contentDocument.body.classList.add(classNameSpotlight);
        }
      } catch (e) {}
    }
  }, [isSpotlightActive]);

  // Set default title on load and synchronize initial URL
  useEffect(() => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('pt-BR');
    const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setVideoTitle(`Gravação Manual — ${dateStr} ${timeStr}`);
    
    const params = new URLSearchParams(window.location.search);
    if (!params.has('url')) {
      try {
        const mode = params.get('mode');
        const modePart = mode ? `&mode=${encodeURIComponent(mode)}` : '';
        const newUrl = `${window.location.pathname}?url=${encodeURIComponent('/')}${modePart}`;
        window.history.replaceState(null, '', newUrl);
      } catch (err) {
        console.warn('Erro ao inicializar a URL do navegador:', err);
      }
    }
  }, []);

  // Auto-run IA extraction when the feedback modal is opened in recorder
  useEffect(() => {
    if (showFeedbackModalInRecorder && !feedbackTitle && !feedbackDesc && !iaGenerating) {
      setIaGenerating(true);
      toast.promise(
        api.post('/feedback/analisar-ia', {
          text: recorder.videoScript || '',
          pageUrl: iframeUrl
        }).then((res) => {
          if (res.data?.ok) {
            setFeedbackTitle(res.data.titulo);
            setFeedbackDesc(res.data.descricao);
          } else {
            throw new Error('Falha na análise');
          }
          setIaGenerating(false);
        }).catch((err) => {
          setIaGenerating(false);
          throw err;
        }),
        {
          loading: 'Extraindo legenda do vídeo e analisando comportamento com IA...',
          success: 'Legenda extraída e feedback auto-preenchido com sucesso!',
          error: 'Erro ao analisar vídeo com IA'
        }
      );
    }
  }, [showFeedbackModalInRecorder, iframeUrl, recorder.videoScript]);

  // Copy auth session variables to iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      try {
        const lowerUrl = iframeUrl.toLowerCase();
        const isPublic = lowerUrl === '/' || 
                         lowerUrl.startsWith('/login') || 
                         lowerUrl.includes('/ativar') || 
                         lowerUrl.includes('/exemplos') || 
                         lowerUrl.includes('/tv') || 
                         lowerUrl.includes('/demo') || 
                         lowerUrl.includes('/download') || 
                         lowerUrl.includes('/install') || 
                         lowerUrl.includes('/security/');

        if (isPublic) {
          try {
            iframe.contentWindow.sessionStorage.clear();
            iframe.contentWindow.localStorage.clear();
          } catch (e) {
            console.warn('Erro ao limpar storage de rota pública:', e);
          }
        } else {
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('ac_')) {
              const val = sessionStorage.getItem(key);
              if (val !== null) {
                iframe.contentWindow.sessionStorage.setItem(key, val);
              }
            }
          }
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('ac_')) {
              const val = localStorage.getItem(key);
              if (val !== null) {
                iframe.contentWindow.localStorage.setItem(key, val);
              }
            }
          }
        }
        iframe.contentWindow.location.replace(iframeUrl);
      } catch (e) {
        console.warn('Erro ao sincronizar sessão inicial do iframe:', e);
        try {
          iframe.contentWindow.location.replace(iframeUrl);
        } catch (err) {}
      }
    }
  }, [iframeUrl]);

  // ── Fetch E-CAD Catalog ──
  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
      const res = await api.get('/ecad/catalogo?includeEmpty=true', { headers });
      if (res.data && res.data.ok) {
        setCatalog(res.data.catalog || []);
      } else if (Array.isArray(res.data)) {
        setCatalog(res.data);
      }
    } catch (err) {
      console.error('Erro ao carregar catálogo', err);
    } finally {
      setLoadingCatalog(false);
    }
  }, [masterToken]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  // ── Initialize extracted hooks (Fase 2) ──
  const catalogCrud = useCatalogCrud({
    masterToken,
    catalog,
    selectedPerfilId, setSelectedPerfilId,
    selectedTopicoId, setSelectedTopicoId,
    selectedSubtopicoId, setSelectedSubtopicoId,
    loadCatalog
  });

  const segmentPlayback = useSegmentPlayback({
    masterToken,
    previewUrl: recorder.previewUrl,
    processedVideoUrl: recorder.processedVideoUrl
  });

  // Close configurations dropdown clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (configMenuRef.current && !configMenuRef.current.contains(e.target as Node)) {
        setConfigMenuOpen(false);
      }
    };
    if (configMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [configMenuOpen]);

  // Address bar routing navigate helper
  const handleNavigateAddressBar = (targetUrl: string) => {
    if (!targetUrl) return;
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        const isActivationLink = targetUrl.toLowerCase().includes('ativar') || targetUrl.toLowerCase().includes('ativar.html');
        const forceLogout = isActivationLink || forceLogoutOnActivation;
        
        if (forceLogout) {
          try {
            iframe.contentWindow.sessionStorage.clear();
            iframe.contentWindow.localStorage.clear();
          } catch (e) {
            console.warn('Erro ao limpar storage do iframe:', e);
          }
        } else {
          try {
            for (let i = 0; i < sessionStorage.length; i++) {
              const key = sessionStorage.key(i);
              if (key && key.startsWith('ac_')) {
                const val = sessionStorage.getItem(key);
                if (val !== null) {
                  iframe.contentWindow.sessionStorage.setItem(key, val);
                }
              }
            }
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith('ac_')) {
                const val = localStorage.getItem(key);
                if (val !== null) {
                  iframe.contentWindow.localStorage.setItem(key, val);
                }
              }
            }
          } catch (e) {}
        }
        
        iframe.contentWindow.location.href = targetUrl;
        setAddressBarUrl(targetUrl);
      }
    } catch (err) {
      console.error('Erro ao navegar no iframe:', err);
      toast.error('Erro ao navegar. O endereço pode ser de outro domínio.');
    }
  };

  const activePerfil = catalog.find(p => p.id === selectedPerfilId);
  const activeTopico = activePerfil?.topicos.find(t => t.id === selectedTopicoId);

  // ── Spotlight insertion helper ──
  const insertIntermediateSpotlight = (index: number, clientX: number, clientY: number, isIframeEvent: boolean) => {
    const W = window.innerWidth;
    const H = statusRef.current === 'recording' ? window.innerHeight : window.innerHeight - 56;
    const y_offset = (statusRef.current === 'recording') ? 0 : 56;
    
    let x = clientX;
    let y = clientY;
    
    if (isIframeEvent) {
      if (isZoomedRef.current && zoomEnabledRef.current) {
        const scaleVal = zoomScaleRef.current;
        x = W / 2 + scaleVal * (x + zoomTxRef.current - W / 2);
        y = y_offset + H / 2 + scaleVal * (y + zoomTyRef.current - H / 2);
      } else {
        y += y_offset;
      }
    }
    x = Math.max(0, Math.min(W, x));
    y = Math.max(0, Math.min(H + y_offset, y));

    const newSpotlight: LockedSpotlight = {
      id: Math.random().toString(36).substring(2, 11),
      shape: 'circle',
      x: x,
      y: y,
      radius: spotlightRadius,
      w: spotlightRadius * 2,
      h: spotlightRadius * 2,
      sequence: index + 2
    };

    setLockedSpotlights(prev => {
      const sorted = [...prev].sort((a, b) => a.sequence - b.sequence);
      const before = sorted.slice(0, index + 1);
      const after = sorted.slice(index + 1);
      const combined = [...before, newSpotlight, ...after];
      return combined.map((item, idx) => ({
        ...item,
        sequence: idx + 1
      }));
    });
  };

  // ══════════════════════════════════════════════════════
  // ── Mouse Move tracking logic ──
  // ══════════════════════════════════════════════════════
  const handleMouseMove = (e: MouseEvent, isIframe = false) => {
    // Guard: phase check + throttle gate (8ms min = 125fps max, auto-throttled by perf mode)
    if (!health.isPhase('RECORDING', 'PAUSED')) return;
    if (!health.gate('handleMouseMove', 8)) return;
    const st = statusRef.current;
    if (st !== 'recording' && st !== 'paused') return;

    let x = e.clientX;
    let y = e.clientY;
    
    const W = window.innerWidth;
    const H = st === 'recording' ? window.innerHeight : window.innerHeight - 56;
    const y_offset = (st === 'recording') ? 0 : 56;

    if (isIframe) {
      if (isZoomedRef.current && zoomEnabledRef.current) {
        const scaleVal = zoomScaleRef.current;
        x = W / 2 + scaleVal * (x + zoomTxRef.current - W / 2);
        y = y_offset + H / 2 + scaleVal * (y + zoomTyRef.current - H / 2);
      } else {
        y += y_offset;
      }
    }
    
    x = Math.max(0, Math.min(W, x));
    y = Math.max(0, Math.min(H + y_offset, y));

    const newPos = { x, y };
    // 🔴 PERF: Update ref + DOM directly — NO setCursorPos (avoids re-render)
    cursorPosRef.current = newPos;
    // Update cursor element position directly
    if (cursorElRef.current) {
      cursorElRef.current.style.left = x + 'px';
      cursorElRef.current.style.top = y + 'px';
      cursorElRef.current.style.display = 'block';
    }
    showCursorRef.current = true;

    const isSpotlightActiveState = isSpotlightActive && (statusRef.current === 'recording' || statusRef.current === 'paused');

    if (!isSpotlightActiveState) {
      if (snappedRect) {
        setSnappedRect(null);
      }
    } else {
      if (isIframe) {
        const target = e.target as HTMLElement;
        const controlEl = getControlAncestor(target);
        
        if (controlEl && autoSnapEnabledRef.current) {
          if (lastSnappedElementRef.current !== controlEl) {
            lastSnappedElementRef.current = controlEl;
          }
          
          const rect = controlEl.getBoundingClientRect();
          const computedStyle = e.view ? e.view.getComputedStyle(controlEl) : null;
          const rawBorderRadius = computedStyle?.borderRadius ? parseFloat(computedStyle.borderRadius) : 8;
          const rx = isNaN(rawBorderRadius) ? 8 : Math.max(4, Math.min(16, rawBorderRadius));
          
          let x1 = rect.left;
          let y1 = rect.top;
          let x2 = rect.right;
          let y2 = rect.bottom;
          
          if (isZoomedRef.current && zoomEnabledRef.current) {
            const scaleVal = zoomScaleRef.current;
            x1 = W / 2 + scaleVal * (x1 + zoomTxRef.current - W / 2);
            y1 = y_offset + H / 2 + scaleVal * (y1 + zoomTyRef.current - H / 2);
            x2 = W / 2 + scaleVal * (x2 + zoomTxRef.current - W / 2);
            y2 = y_offset + H / 2 + scaleVal * (y2 + zoomTyRef.current - H / 2);
          } else {
            y1 += y_offset;
            y2 += y_offset;
          }
          
          setSnappedRect({ x: x1, y: y1, w: x2 - x1, h: y2 - y1, rx });
        } else {
          if (lastSnappedElementRef.current && !controlEl) {
            lastSnappedElementRef.current = null;
            setAutoSnapEnabled(true);
          }
          setSnappedRect(null);
        }
      } else {
        setSnappedRect(null);
      }
    }

    let hoveredId: string | null = null;
    if (isSpotlightActiveState) {
      const idx = lockedSpotlightsRef.current.findIndex(item => {
        if (item.shape === 'circle') {
          const dx = x - item.x;
          const dy = y - item.y;
          return Math.sqrt(dx * dx + dy * dy) <= item.radius;
        } else {
          return x >= item.x && x <= item.x + item.w && y >= item.y && y <= item.y + item.h;
        }
      });
      if (idx !== -1) {
        hoveredId = lockedSpotlightsRef.current[idx].id;
      }
    }
    
    if (hoveredSpotlightIdRef.current !== hoveredId) {
      setHoveredSpotlightId(hoveredId);
      hoveredSpotlightIdRef.current = hoveredId;
      
      if (deletePendingSpotlightIdRef.current && deletePendingSpotlightIdRef.current !== hoveredId) {
        setDeletePendingSpotlightId(null);
      }
    }

    if (isSpotlightActiveState && rightStartPosRef.current) {
      const start = rightStartPosRef.current;
      const dx = x - start.x;
      const dy = y - start.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      if (dist > 5) {
        isRightDraggingRef.current = true;
        const minX = Math.min(start.x, x);
        const maxX = Math.max(start.x, x);
        const minY = Math.min(start.y, y);
        const maxY = Math.max(start.y, y);
        
        setCustomSpotlight({
          x: minX,
          y: minY,
          w: maxX - minX,
          h: maxY - minY
        });
      }
    }

    if (isSpotlightActiveState && maybeDraggingSpotlightIdRef.current) {
      const start = dragStartCursorPosRef.current;
      const dx = x - start.x;
      const dy = y - start.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      if (dist > 6) {
        const id = maybeDraggingSpotlightIdRef.current;
        setDraggedSpotlightId(id);
        draggedSpotlightIdRef.current = id;
        maybeDraggingSpotlightIdRef.current = null;
      }
    }

    if (draggedSpotlightIdRef.current) {
      e.preventDefault();
      e.stopPropagation();
      const dx = x - lastCursorPosRef.current.x;
      const dy = y - lastCursorPosRef.current.y;
      
      if (dx !== 0 || dy !== 0) {
        setLockedSpotlights(prev => prev.map(s => {
          if (s.id === draggedSpotlightIdRef.current) {
            return { ...s, x: s.x + dx, y: s.y + dy };
          }
          return s;
        }));
      }
    }
    
    lastCursorPosRef.current = { x, y };
  };

  // ── Mouse Down Click ──
  const handleMouseDown = (e: MouseEvent, isIframe = false) => {
    if (!health.isPhase('RECORDING', 'PAUSED')) return;
    if (!health.gate('handleMouseDown', 50)) return;
    const st = statusRef.current;
    if (st !== 'recording' && st !== 'paused') return;

    if (deletePendingSpotlightIdRef.current) {
      setDeletePendingSpotlightId(null);
    }
    let x = e.clientX;
    let y = e.clientY;
    
    const W = window.innerWidth;
    const H = st === 'recording' ? window.innerHeight : window.innerHeight - 56;
    const y_offset = (st === 'recording') ? 0 : 56;

    if (isIframe) {
      if (isZoomedRef.current && zoomEnabledRef.current) {
        const scaleVal = zoomScaleRef.current;
        x = W / 2 + scaleVal * (x + zoomTxRef.current - W / 2);
        y = y_offset + H / 2 + scaleVal * (y + zoomTyRef.current - H / 2);
      } else {
        y += y_offset;
      }
    }
    
    x = Math.max(0, Math.min(W, x));
    y = Math.max(0, Math.min(H + y_offset, y));

    const isSpotlightActiveState = isSpotlightActive && (statusRef.current === 'recording' || statusRef.current === 'paused');

    if (e.button === 0 && isSpotlightActiveState) {
      const clickedInsideIndex = lockedSpotlightsRef.current.findIndex(item => {
        if (item.shape === 'circle') {
          const dx = x - item.x;
          const dy = y - item.y;
          return Math.sqrt(dx * dx + dy * dy) <= item.radius;
        } else {
          return x >= item.x && x <= item.x + item.w && y >= item.y && y <= item.y + item.h;
        }
      });
      if (clickedInsideIndex !== -1) {
        maybeDraggingSpotlightIdRef.current = lockedSpotlightsRef.current[clickedInsideIndex].id;
        dragStartCursorPosRef.current = { x, y };
        lastCursorPosRef.current = { x, y };
      } else if (snappedRect) {
        const newSpotlight: LockedSpotlight = {
          id: Math.random().toString(36).substring(2, 11),
          shape: 'square',
          x: snappedRect.x - 4,
          y: snappedRect.y - 4,
          w: snappedRect.w + 8,
          h: snappedRect.h + 8,
          radius: snappedRect.rx + 2,
          sequence: lockedSpotlightsRef.current.length + 1
        };
        
        const alreadyExists = lockedSpotlightsRef.current.some(s => 
          Math.abs(s.x - newSpotlight.x) < 8 && Math.abs(s.y - newSpotlight.y) < 8
        );
        
        if (!alreadyExists) {
          setLockedSpotlights(prev => [...prev, newSpotlight]);
        }
      }
    }

    if (e.button === 2 && isSpotlightActiveState) {
      e.preventDefault();
      rightStartPosRef.current = { x, y };
      isRightDraggingRef.current = false;
      setCustomSpotlight(null);
      return;
    }

    const target = e.target as HTMLElement;
    if (target && statusRef.current === 'recording') {
      const selector = getCleanSelector(target);
      const text = target.innerText?.trim().substring(0, 30) || target.getAttribute('placeholder') || target.getAttribute('title') || '';
      const meta = getElementMetadata(target);
      recorder.recordTimelineEvent('click', selector, text ? `Clicou em "${text}"` : 'Clicou', meta);
    }
    
    if (statusRef.current === 'recording' || statusRef.current === 'paused') {
      recorder.playBeep('click');
    }
    
    const id = Date.now() + Math.random();
    setClickRipples(prev => [...prev, { id, x, y }]);
    
    setTimeout(() => {
      setClickRipples(prev => prev.filter(r => r.id !== id));
    }, 450);
  };

  const handleMouseLeave = () => {
    if (!health.isPhase('RECORDING', 'PAUSED')) return;
    if (!health.gate('handleMouseLeave', 50)) return;
    const st = statusRef.current;
    if (st !== 'recording' && st !== 'paused') return;
    // 🔴 PERF: Hide cursor via DOM, not state
    showCursorRef.current = false;
    if (cursorElRef.current) cursorElRef.current.style.display = 'none';
    rightStartPosRef.current = null;
    isRightDraggingRef.current = false;
    setHoveredSpotlightId(null);
    setDeletePendingSpotlightId(null);
  };

  const handleIframeMouseMove = (e: MouseEvent) => {
    handleMouseMove(e, true);
  };

  const handleIframeMouseDown = (e: MouseEvent) => {
    handleMouseDown(e, true);
  };

  // ── Iframe style injection ──
  const injectIframeStyles = () => {
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentDocument) {
        const doc = iframe.contentDocument;
        if (!doc.getElementById('ac-recorder-styles')) {
          const style = doc.createElement('style');
          style.id = 'ac-recorder-styles';
          style.textContent = `
            body.ac-recording-active,
            body.ac-recording-active * {
              cursor: none !important;
            }
            body.ac-spotlight-active .modal-overlay,
            body.ac-spotlight-active [class*="modal-backdrop"],
            body.ac-spotlight-active [class*="backdrop-overlay"],
            body.ac-spotlight-active [class*="dialog-overlay"] {
              background: transparent !important;
              backdrop-filter: none !important;
              -webkit-backdrop-filter: none !important;
            }
          `;
          doc.head.appendChild(style);
        }
      }
    } catch (e) {}
  };

  const updateCursorVisibility = (hiding: boolean) => {
    const className = 'ac-recording-active';
    if (hiding) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
        if (hiding) {
          iframe.contentDocument.body.classList.add(className);
        } else {
          iframe.contentDocument.body.classList.remove(className);
        }
      }
    } catch (e) {}
  };

  // NOTE: updateCursorVisibility is now called inside deactivateRecordingMode/activateRecordingMode

  // ══════════════════════════════════════════════════════
  // ── Keyboard handlers ──
  // ══════════════════════════════════════════════════════
  const handleKeystrokeVisualizer = (e: KeyboardEvent) => {
    if (!keystrokesEnabled) return;
    if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Meta') {
      return;
    }
    const isModifier = e.ctrlKey || e.altKey || e.shiftKey || e.metaKey;
    const isActionKey = ['Enter', 'Escape', 'Tab', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
    
    if (isModifier || isActionKey) {
      const parts = [];
      if (e.ctrlKey) parts.push('Ctrl');
      if (e.altKey) parts.push('Alt');
      if (e.shiftKey) parts.push('Shift');
      if (e.metaKey) parts.push('Meta');
      parts.push(e.key);
      
      const combination = parts.join(' + ');
      setLastKeys(combination);
      setShowKeys(true);
      
      const timerId = (window as any)._keysTimer;
      if (timerId) clearTimeout(timerId);
      (window as any)._keysTimer = setTimeout(() => {
        setShowKeys(false);
      }, 1500);
    }
  };

  const checkIsInput = (el: HTMLElement | null): boolean => {
    if (!el) return false;
    const tag = (el.tagName || '').toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true) {
      return true;
    }
    if (tag === 'IFRAME') {
      try {
        const iframe = el as HTMLIFrameElement;
        if (iframe.contentDocument && iframe.contentDocument.activeElement) {
          return checkIsInput(iframe.contentDocument.activeElement as HTMLElement);
        }
      } catch (e) {}
    }
    return false;
  };

  const isEditingText = (target: HTMLElement | null): boolean => {
    if (checkIsInput(target)) return true;
    if (typeof document !== 'undefined' && checkIsInput(document.activeElement as HTMLElement)) {
      return true;
    }
    try {
      if (iframeRef.current && checkIsInput(iframeRef.current)) {
        return true;
      }
    } catch (e) {}
    return false;
  };

  const handleGlobalKeyDown = (e: KeyboardEvent) => {
    const currentStatus = statusRef.current;

    // Global Ctrl+F9 shortcut handler inside the recorder
    if (e.ctrlKey && e.key === 'F9') {
      e.preventDefault();
      if (currentStatus === 'recording' || currentStatus === 'paused') {
        recorder.handleStop();
      } else {
        handleExit();
      }
      return;
    }

    if (currentStatus === 'recording' || currentStatus === 'paused') {
      const isInput = isEditingText(e.target as HTMLElement);

      if (e.key === 'F10') {
        e.preventDefault();
        if (currentStatus === 'recording') {
          recorder.handlePause();
        } else {
          recorder.handleResume();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        recorder.handleStop();
      } else if (e.key === 'F4') {
        e.preventDefault();
        if (isFeedbackMode) {
          recorder.handleStop();
        } else {
          window.dispatchEvent(new CustomEvent('ecad-toggle-status-popup'));
        }
      } else if (e.key === 'Shift') {
        if (isInput) return;
        if (zoomEnabledRef.current && !e.repeat) {
          const willZoom = !isZoomedRef.current;
          if (willZoom) {
            const W = window.innerWidth;
            const H = statusRef.current === 'recording' ? window.innerHeight : window.innerHeight - 56;
            const scaleVal = zoomScaleRef.current;
            const limitX = W * (scaleVal - 1) / (2 * scaleVal);
            const limitY = H * (scaleVal - 1) / (2 * scaleVal);
            zoomTxRef.current = Math.max(-limitX, Math.min(limitX, W / 2 - cursorPosRef.current.x));
            zoomTyRef.current = Math.max(-limitY, Math.min(limitY, H / 2 - cursorPosRef.current.y));
          }
          setIsZoomed(willZoom);
        }
      } else if (e.key === 'Control') {
        if (isInput) return;
        if (recorder.useMic && !e.repeat) {
          ctrlPressTimeRef.current = Date.now();
          if (spotlightModeRef.current === 'off') {
            spotlightModeRef.current = 'holding';
            setIsSpotlightActive(true);
          } else if (spotlightModeRef.current === 'toggled') {
            spotlightModeRef.current = 'off';
            setIsSpotlightActive(false);
          }
        }
      }
      handleKeystrokeVisualizer(e);
    }
  };

  const handleGlobalKeyUp = (e: KeyboardEvent) => {
    const st = statusRef.current;
    if (st !== 'recording' && st !== 'paused') return;

    const isInput = isEditingText(e.target as HTMLElement);
    if (isInput) return;

    if (e.key === 'Control') {
      if (spotlightModeRef.current === 'holding') {
        const elapsed = Date.now() - ctrlPressTimeRef.current;
        if (elapsed < 300) {
          spotlightModeRef.current = 'toggled';
        } else {
          spotlightModeRef.current = 'off';
          setIsSpotlightActive(false);
        }
      }
    }
  };

  const handleWheel = (e: WheelEvent) => {
    if (!health.gate('handleWheel', 16)) return;
    const isZoomActive = isZoomedRef.current && zoomEnabledRef.current;
    const isSpotlightActiveState = isSpotlightActive && (statusRef.current === 'recording' || statusRef.current === 'paused');

    if (isZoomActive) {
      e.preventDefault();
      setZoomScale(prev => {
        const delta = e.deltaY < 0 ? 0.1 : -0.1;
        const next = Math.max(1.2, Math.min(4.0, Number((prev + delta).toFixed(2))));
        localStorage.setItem('ecad_zoom_scale', String(next));
        return next;
      });
    } else if (isSpotlightActiveState) {
      e.preventDefault();
      const hoveredId = hoveredSpotlightIdRef.current;
      if (hoveredId) {
        setLockedSpotlights(prev => prev.map(s => {
          if (s.id === hoveredId) {
            const delta = e.deltaY < 0 ? 10 : -10;
            if (s.shape === 'circle') {
              const nextRadius = Math.max(30, Math.min(300, s.radius + delta));
              return { ...s, radius: nextRadius, w: nextRadius * 2, h: nextRadius * 2 };
            } else {
              const nextW = Math.max(40, Math.min(600, s.w + delta));
              const nextH = Math.max(40, Math.min(600, s.h + delta));
              const dx = (nextW - s.w) / 2;
              const dy = (nextH - s.h) / 2;
              return { ...s, x: s.x - dx, y: s.y - dy, w: nextW, h: nextH };
            }
          }
          return s;
        }));
      } else {
        setSpotlightRadius(prev => {
          const delta = e.deltaY < 0 ? 10 : -10;
          const next = Math.max(30, Math.min(300, prev + delta));
          localStorage.setItem('ecad_spotlight_radius', String(next));
          return next;
        });
      }
    }
  };

  const handleContextMenu = (e: MouseEvent) => {
    const isSpotlightActiveState = isSpotlightActive && (statusRef.current === 'recording' || statusRef.current === 'paused');
    if (isSpotlightActiveState) {
      e.preventDefault();
    }
  };

  const handleMouseUp = (e: MouseEvent) => {
    if (!health.gate('handleMouseUp', 50)) return;
    const st = statusRef.current;
    if (st !== 'recording' && st !== 'paused') return;

    maybeDraggingSpotlightIdRef.current = null;
    if (draggedSpotlightIdRef.current) {
      e.preventDefault();
      e.stopPropagation();
      setDraggedSpotlightId(null);
      rightStartPosRef.current = null;
      isRightDraggingRef.current = false;
      setCustomSpotlight(null);
      return;
    }
    const isSpotlightActiveState = isSpotlightActive && (statusRef.current === 'recording' || statusRef.current === 'paused');

    if (e.button === 2 && isSpotlightActiveState && rightStartPosRef.current) {
      e.preventDefault();
      
      if (snappedRect) {
        setAutoSnapEnabled(false);
        setSnappedRect(null);
        rightStartPosRef.current = null;
        isRightDraggingRef.current = false;
        setCustomSpotlight(null);
        return;
      }
      
      if (clickHandledByArrowRef.current) {
        clickHandledByArrowRef.current = false;
        rightStartPosRef.current = null;
        isRightDraggingRef.current = false;
        setCustomSpotlight(null);
        return;
      }
      
      const isIframeEvent = e.view !== window;
      const clientX = e.clientX;
      const clientY = e.clientY;
      const W = window.innerWidth;
      const H = recorder.status === 'recording' ? window.innerHeight : window.innerHeight - 56;
      const y_offset = recorder.status === 'recording' ? 0 : 56;
      
      let x = clientX;
      let y = clientY;
      
      if (isIframeEvent) {
        if (isZoomedRef.current && zoomEnabledRef.current) {
          const scaleVal = zoomScaleRef.current;
          x = W / 2 + scaleVal * (x + zoomTxRef.current - W / 2);
          y = y_offset + H / 2 + scaleVal * (y + zoomTyRef.current - H / 2);
        } else {
          y += y_offset;
        }
      }
      x = Math.max(0, Math.min(W, x));
      y = Math.max(0, Math.min(H + y_offset, y));
      
      const clickedInsideIndex = lockedSpotlightsRef.current.findIndex(item => {
        if (item.shape === 'circle') {
          const dx = x - item.x;
          const dy = y - item.y;
          return Math.sqrt(dx * dx + dy * dy) <= item.radius;
        } else {
          return x >= item.x && x <= item.x + item.w && y >= item.y && y <= item.y + item.h;
        }
      });

      if (clickedInsideIndex !== -1) {
        const item = lockedSpotlightsRef.current[clickedInsideIndex];
        setDeletePendingSpotlightId(item.id);
      } else {
        if (!isRightDraggingRef.current) {
          const newSpotlight: LockedSpotlight = {
            id: Math.random().toString(36).substring(2, 11),
            shape: 'circle',
            x: x,
            y: y,
            radius: spotlightRadius,
            w: spotlightRadius * 2,
            h: spotlightRadius * 2,
            sequence: lockedSpotlightsRef.current.length + 1
          };
          setLockedSpotlights(prev => [...prev, newSpotlight]);
        } else {
          const start = rightStartPosRef.current;
          const minX = Math.min(start.x, x);
          const maxX = Math.max(start.x, x);
          const minY = Math.min(start.y, y);
          const maxY = Math.max(start.y, y);
          const w = maxX - minX;
          const h = maxY - minY;
          
          if (w > 5 && h > 5) {
            const newSpotlight: LockedSpotlight = {
              id: Math.random().toString(36).substring(2, 11),
              shape: 'square',
              x: minX,
              y: minY,
              w: w,
              h: h,
              radius: Math.max(w, h) / 2,
              sequence: lockedSpotlightsRef.current.length + 1
            };
            setLockedSpotlights(prev => [...prev, newSpotlight]);
          }
        }
      }
      
      rightStartPosRef.current = null;
      isRightDraggingRef.current = false;
      setCustomSpotlight(null);
    }
  };

  // ══════════════════════════════════════════════════════
  // ── Stable event handler refs ──
  // ══════════════════════════════════════════════════════
  const handleMouseUpRef = useRef(handleMouseUp);
  handleMouseUpRef.current = handleMouseUp;
  const stableMouseUp = useCallback((e: MouseEvent) => { handleMouseUpRef.current(e); }, []);

  const handleWheelRef = useRef(handleWheel);
  handleWheelRef.current = handleWheel;
  const stableWheel = useCallback((e: WheelEvent) => { handleWheelRef.current(e); }, []);

  const handleContextMenuRef = useRef(handleContextMenu);
  handleContextMenuRef.current = handleContextMenu;
  const stableContextMenu = useCallback((e: MouseEvent) => { handleContextMenuRef.current(e); }, []);

  const handleGlobalKeyDownRef = useRef(handleGlobalKeyDown);
  handleGlobalKeyDownRef.current = handleGlobalKeyDown;
  const stableKeyDown = useCallback((e: KeyboardEvent) => { handleGlobalKeyDownRef.current(e); }, []);

  const handleGlobalKeyUpRef = useRef(handleGlobalKeyUp);
  handleGlobalKeyUpRef.current = handleGlobalKeyUp;
  const stableKeyUp = useCallback((e: KeyboardEvent) => { handleGlobalKeyUpRef.current(e); }, []);

  const handleIframeMouseMoveRef = useRef(handleIframeMouseMove);
  handleIframeMouseMoveRef.current = handleIframeMouseMove;
  const stableIframeMouseMove = useCallback((e: MouseEvent) => { handleIframeMouseMoveRef.current(e); }, []);

  const handleIframeMouseDownRef = useRef(handleIframeMouseDown);
  handleIframeMouseDownRef.current = handleIframeMouseDown;
  const stableIframeMouseDown = useCallback((e: MouseEvent) => { handleIframeMouseDownRef.current(e); }, []);

  const handleMouseLeaveRef = useRef(handleMouseLeave);
  handleMouseLeaveRef.current = handleMouseLeave;
  const stableMouseLeave = useCallback(() => { handleMouseLeaveRef.current(); }, []);

  const handleDragStart = (e: any) => {
    const isSpotlightActiveState = isSpotlightActive && (statusRef.current === 'recording' || statusRef.current === 'paused');
    if (isSpotlightActiveState && (rightStartPosRef.current || draggedSpotlightIdRef.current)) {
      e.preventDefault();
    }
  };
  const handleDragStartRef = useRef(handleDragStart);
  handleDragStartRef.current = handleDragStart;
  const stableDragStart = useCallback((e: any) => { handleDragStartRef.current(e); }, []);

  const handleSelectStart = (e: Event) => {
    const isSpotlightActiveState = isSpotlightActive && (statusRef.current === 'recording' || statusRef.current === 'paused');
    if (isSpotlightActiveState && (rightStartPosRef.current || draggedSpotlightIdRef.current)) {
      e.preventDefault();
    }
  };
  const handleSelectStartRef = useRef(handleSelectStart);
  handleSelectStartRef.current = handleSelectStart;
  const stableSelectStart = useCallback((e: Event) => { handleSelectStartRef.current(e); }, []);

  const handleBlur = () => {
    const st = statusRef.current;
    if (st !== 'recording' && st !== 'paused') return;
    rightStartPosRef.current = null;
    isRightDraggingRef.current = false;
    maybeDraggingSpotlightIdRef.current = null;
    setDraggedSpotlightId(null);
    setDeletePendingSpotlightId(null);
    setHoveredSpotlightId(null);
  };
  const handleBlurRef = useRef(handleBlur);
  handleBlurRef.current = handleBlur;
  const stableBlur = useCallback(() => { handleBlurRef.current(); }, []);

  // Stable refs for parent-window mouse handlers (prevent useEffect deps from causing re-bind)
  const handleMouseMoveRef = useRef(handleMouseMove);
  handleMouseMoveRef.current = handleMouseMove;
  const stableParentMouseMove = useCallback((e: MouseEvent) => { handleMouseMoveRef.current(e, false); }, []);

  const handleMouseDownRef = useRef(handleMouseDown);
  handleMouseDownRef.current = handleMouseDown;
  const stableParentMouseDown = useCallback((e: MouseEvent) => { handleMouseDownRef.current(e, false); }, []);

  // ── Bind keydown, keyup and mouse tracking to parent window ──
  useEffect(() => {
    window.addEventListener('keydown', stableKeyDown);
    window.addEventListener('keyup', stableKeyUp);
    window.addEventListener('mousemove', stableParentMouseMove);
    window.addEventListener('mousedown', stableParentMouseDown);
    window.addEventListener('mouseleave', stableMouseLeave);
    window.addEventListener('wheel', stableWheel, { passive: false });
    window.addEventListener('contextmenu', stableContextMenu);
    window.addEventListener('mouseup', stableMouseUp);
    window.addEventListener('dragstart', stableDragStart);
    window.addEventListener('selectstart', stableSelectStart);
    window.addEventListener('blur', stableBlur);
    
    return () => {
      window.removeEventListener('keydown', stableKeyDown);
      window.removeEventListener('keyup', stableKeyUp);
      window.removeEventListener('mousemove', stableParentMouseMove);
      window.removeEventListener('mousedown', stableParentMouseDown);
      window.removeEventListener('mouseleave', stableMouseLeave);
      window.removeEventListener('wheel', stableWheel);
      window.removeEventListener('contextmenu', stableContextMenu);
      window.removeEventListener('mouseup', stableMouseUp);
      window.removeEventListener('dragstart', stableDragStart);
      window.removeEventListener('selectstart', stableSelectStart);
      window.removeEventListener('blur', stableBlur);
    };
  }, []); // ← mount-once: all handlers use stable refs

  // ── Detach events from iframe (cleanup) ──
  const detachIframeEvents = () => {
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        const win = iframe.contentWindow;
        win.removeEventListener('keydown', stableKeyDown);
        win.removeEventListener('keyup', stableKeyUp);
        win.removeEventListener('mousemove', stableIframeMouseMove, { capture: true } as any);
        win.removeEventListener('mousedown', stableIframeMouseDown, { capture: true } as any);
        win.removeEventListener('mouseleave', stableMouseLeave);
        win.removeEventListener('wheel', stableWheel, { capture: true } as any);
        win.removeEventListener('contextmenu', stableContextMenu);
        win.removeEventListener('mouseup', stableMouseUp, { capture: true } as any);
        win.removeEventListener('dragstart', stableDragStart);
        win.removeEventListener('selectstart', stableSelectStart);
        win.removeEventListener('blur', stableBlur);
      }
      iframeObstaclesRef.current = [];
    } catch (err) {
      // iframe may have been navigated away — safe to ignore
    }
  };

  // ── Reset iframe to about:blank (kill du-agent, React app, all resources) ──
  const resetIframe = useCallback(() => {
    try {
      detachIframeEvents();
      const iframe = iframeRef.current;
      if (iframe) {
        iframe.src = 'about:blank';
      }
    } catch (err) {
      console.warn('[ECAD] Erro ao resetar iframe:', err);
    }
  }, []);

  // ── Bind events to iframe ──
  const attachIframeEvents = () => {
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow && iframe.contentDocument) {
        const win = iframe.contentWindow;
        const doc = iframe.contentDocument;

        try {
          const W = window.innerWidth;
          const H = statusRef.current === 'recording' ? window.innerHeight : window.innerHeight - 56;
          const y_offset = (statusRef.current === 'recording') ? 0 : 56;
          const scaleVal = zoomScaleRef.current;
          const isZoomActive = isZoomedRef.current && zoomEnabledRef.current;

          const elements = doc.querySelectorAll('input, select, textarea, button, a, [role="button"], [role="link"]');
          const obstaclesList: Array<{ x: number; y: number; w: number; h: number; id: string }> = [];
          
          elements.forEach((el, idx) => {
            const htmlEl = el as HTMLElement;
            if (htmlEl.id === 'ac-fake-cursor') return;

            const rect = htmlEl.getBoundingClientRect();
            if (rect.width > 5 && rect.height > 5) {
              const computedStyle = win.getComputedStyle(htmlEl);
              if (computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden' && computedStyle.opacity !== '0') {
                let x1 = rect.left;
                let y1 = rect.top;
                let x2 = rect.right;
                let y2 = rect.bottom;
                
                if (isZoomActive) {
                  x1 = W / 2 + scaleVal * (x1 + zoomTxRef.current - W / 2);
                  y1 = y_offset + H / 2 + scaleVal * (y1 + zoomTyRef.current - H / 2);
                  x2 = W / 2 + scaleVal * (x2 + zoomTxRef.current - W / 2);
                  y2 = y_offset + H / 2 + scaleVal * (y2 + zoomTyRef.current - H / 2);
                } else {
                  y1 += y_offset;
                  y2 += y_offset;
                }
                
                obstaclesList.push({
                  x: x1 - 4, y: y1 - 4,
                  w: (x2 - x1) + 8, h: (y2 - y1) + 8,
                  id: `iframe-ctrl-${idx}`
                });
              }
            }
          });
          iframeObstaclesRef.current = obstaclesList;
        } catch (e) {}
        
        win.removeEventListener('keydown', stableKeyDown);
        win.addEventListener('keydown', stableKeyDown);
        win.removeEventListener('keyup', stableKeyUp);
        win.addEventListener('keyup', stableKeyUp);
        win.removeEventListener('mousemove', stableIframeMouseMove, { capture: true });
        win.addEventListener('mousemove', stableIframeMouseMove, { capture: true });
        win.removeEventListener('mousedown', stableIframeMouseDown, { capture: true });
        win.addEventListener('mousedown', stableIframeMouseDown, { capture: true });
        win.removeEventListener('mouseleave', stableMouseLeave);
        win.addEventListener('mouseleave', stableMouseLeave);
        win.removeEventListener('wheel', stableWheel, { capture: true });
        win.addEventListener('wheel', stableWheel, { capture: true, passive: false });
        win.removeEventListener('contextmenu', stableContextMenu);
        win.addEventListener('contextmenu', stableContextMenu);
        win.removeEventListener('mouseup', stableMouseUp, { capture: true });
        win.addEventListener('mouseup', stableMouseUp, { capture: true });
        win.removeEventListener('dragstart', stableDragStart);
        win.addEventListener('dragstart', stableDragStart);
        win.removeEventListener('selectstart', stableSelectStart);
        win.addEventListener('selectstart', stableSelectStart);
        win.removeEventListener('blur', stableBlur);
        win.addEventListener('blur', stableBlur);

        try {
          const currentPath = win.location.pathname + win.location.search;
          if ((iframe as any)._lastRecordedPath !== currentPath) {
            const oldPath = (iframe as any)._lastRecordedPath;
            (iframe as any)._lastRecordedPath = currentPath;
            
            setAddressBarUrl(currentPath);
            try {
              const searchParams = new URLSearchParams(window.location.search);
              const mode = searchParams.get('mode');
              const modePart = mode ? `&mode=${encodeURIComponent(mode)}` : '';
              const newUrl = `${window.location.pathname}?url=${encodeURIComponent(currentPath)}${modePart}`;
              window.history.replaceState(null, '', newUrl);
            } catch (err) {
              console.warn('Erro ao atualizar a URL do navegador:', err);
            }

            if (statusRef.current === 'recording' && oldPath) {
              const pageTitle = win.document.title || currentPath;
              const routeMeta = {
                title: pageTitle,
                pathname: win.location.pathname,
                search: win.location.search,
                hash: win.location.hash,
                origin: win.location.origin
              };
              recorder.recordTimelineEvent('route', currentPath, `Navegou para "${pageTitle}"`, routeMeta);
            }
          }
        } catch (e) {}

        injectIframeStyles();
        
        const isRecordingOrPaused = statusRef.current === 'recording' || statusRef.current === 'paused';
        const className = 'ac-recording-active';
        if (isRecordingOrPaused) {
          iframe.contentDocument.body.classList.add(className);
        } else {
          iframe.contentDocument.body.classList.remove(className);
        }

        const classNameSpotlight = 'ac-spotlight-active';
        if (isSpotlightActiveRef.current) {
          iframe.contentDocument.body.classList.add(classNameSpotlight);
        } else {
          iframe.contentDocument.body.classList.remove(classNameSpotlight);
        }
      }
    } catch (err) {}
  };

  useEffect(() => {
    let interval: any = null;
    if (recorder.status === 'recording' || recorder.status === 'paused') {
      attachIframeEvents();
      interval = setInterval(attachIframeEvents, 1000);
    } else {
      // Cleanup: remove iframe listeners when not recording
      detachIframeEvents();
    }
    return () => {
      if (interval) clearInterval(interval);
      detachIframeEvents();
    };
  }, [recorder.status]);

  // ══════════════════════════════════════════════════════
  // ── Action handlers ──
  // ══════════════════════════════════════════════════════
  const handleStartRecorder = () => {
    setLockedSpotlights([]);
    setShowManualInput(false);
    recorder.handleStart(() => {
      // ── Cleanup: reset iframe BEFORE opening modal ──
      // Kills du-runtime-agent, React App, MutationObserver,
      // fetch/history patches, and all event listeners inside the iframe
      console.log('[ECAD] 🧹 Resetting iframe before opening AI Studio...');
      resetIframe();

      // Kanban Relay mode: não abrir modal — relay + download + auto-close já estão em andamento
      if (isKanbanRelayMode) {
        console.log('[ECAD] 📡 Modo Kanban Relay — feedback modal suprimido. Auto-close em 2s.');
        return;
      }

      // Small delay to let iframe teardown finish before mounting modal
      setTimeout(() => {
        if (isFeedbackMode) {
          setShowFeedbackModalInRecorder(true);
        } else {
          setIsLinkingModalOpen(true);
        }
      }, 150);
    });
  };

  const handleSynthesizeAction = async () => {
    const success = await recorder.handleSynthesize(selectedSubtopicoId, forceRecreate, selectedModel);
    if (success) {
      setActiveTab('preview');
    }
  };

  const handleRenderStart = async (audioMode: 'bruto' | 'premium', voiceId: string) => {
    if (isRendering) return;
    if (!recorder.previewUrl) {
      toast.error('Nenhum vídeo gravado para renderizar.');
      return;
    }
    if (!recorder.transcriptionSegments || recorder.transcriptionSegments.length === 0) {
      toast.error('Transcrição necessária para renderizar.');
      return;
    }

    setIsRendering(true);
    const vNum = renderedVersions.length + 1;
    toast.loading(`Renderizando v${vNum} (${audioMode === 'bruto' ? 'Original' : 'Premium'})...`, { id: 'render-studio' });

    try {
      const headers = masterToken ? { Authorization: `Bearer ${masterToken}` } : {};
      const res = await api.post('/ecad/renderizar-studio', {
        sessionId: recorder.recordingSessionRef?.current || `session_${Date.now()}`,
        audioMode,
        voiceId: voiceId || undefined,
        modelId: selectedModel,
        segments: recorder.transcriptionSegments.map(seg => {
          const activeAudio = segmentAudio.getActiveVersionForVoice(seg.index, 'premium', voiceId || 'Kore');
          return {
            ...seg,
            audioUrl: activeAudio?.audioUrl || undefined,
          };
        }),
        originalVideoPath: (() => {
          const rawPath = recorder.gcsVideoPath || recorder.previewUrl || '';
          if (rawPath.startsWith('blob:')) {
            return `ecad/recordings/${recorder.recordingSessionRef?.current || ''}/original/video.webm`;
          }
          return rawPath;
        })(),
      }, { headers });

      if (res.data?.ok) {
        const newVersion: RenderVersion = {
          version: res.data.version,
          videoUrl: res.data.videoUrl,
          vttUrl: res.data.vttUrl,
          audioMode: res.data.audioMode,
          voiceId: res.data.voiceId,
          createdAt: res.data.createdAt,
        };
        setRenderedVersions(prev => [...prev, newVersion]);
        setActiveRenderIndex(renderedVersions.length); // new one = last
        toast.success(`Renderização v${res.data.version} concluída!`, { id: 'render-studio' });
        setActiveTab('preview');
      } else {
        toast.error('Falha na renderização.', { id: 'render-studio' });
      }
    } catch (err: any) {
      console.error('[Render]', err);
      toast.error(`Erro: ${err.message || 'falha interna'}`, { id: 'render-studio' });
    } finally {
      setIsRendering(false);
    }
  };

  const handlePublishRemote = async () => {
    if (!selectedSubtopicoId) return;
    setPublishingRemote(true);
    try {
      const res = await api.post(`/ecad/roteiro/${selectedSubtopicoId}/publicar-remoto`);
      if (res.data && res.data.ok) {
        toast.success('Roteiro publicado com sucesso na produção!');
        const prodUrl = `https://alunoconectado.com.br/ecad/${selectedSubtopicoId}`;
        setPublishedUrl(prodUrl);
      } else {
        toast.error('Erro ao publicar roteiro.');
      }
    } catch (err: any) {
      console.error('Erro ao publicar roteiro', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Falha ao comunicar com o servidor.';
      toast.error(`Falha ao publicar: ${errMsg}`);
    } finally {
      setPublishingRemote(false);
    }
  };

  const handleSaveAction = async (vincular: boolean) => {
    if (vincular && (!selectedPerfilId || !selectedTopicoId || !selectedSubtopicoId)) {
      toast.warning('Selecione o perfil, módulo e aula para vincular o vídeo.');
      setActiveTab('catalog');
      return;
    }

    const activeVersion = activeRenderIndex >= 0 && activeRenderIndex < renderedVersions.length
      ? renderedVersions[activeRenderIndex]
      : null;

    const success = await recorder.handleSave(vincular, {
      selectedPerfilId,
      selectedTopicoId,
      selectedSubtopicoId,
      activeTopico,
      activeVideoUrl: activeVersion?.videoUrl || undefined,
      activeVttUrl: activeVersion?.vttUrl || undefined,
    });
    if (success) {
      setLockedSpotlights([]);
      setShowManualInput(false);
      setIsLinkingModalOpen(false);
      if (vincular) {
        setPublishedUrl(null);
        setPublishModalOpen(true);
      }
    }
  };

  const handleDiscardAction = async () => {
    const confirmed = await recorder.handleDiscard();
    if (confirmed) {
      setLockedSpotlights([]);
      setShowManualInput(false);
      setIsLinkingModalOpen(false);
    }
  };

  // ── Fix Overflows: stretch segments so TTS audio fits without cutting ──
  const handleFixOverflows = useCallback(() => {
    const segments = [...recorder.transcriptionSegments];
    if (segments.length === 0) return;

    // Helper: parse MM:SS to seconds
    const toSec = (ts: string): number => {
      const parts = ts.split(':');
      if (parts.length === 3) return (parseFloat(parts[0]) * 3600) + (parseFloat(parts[1]) * 60) + (parseFloat(parts[2]) || 0);
      if (parts.length === 2) return (parseFloat(parts[0]) * 60) + (parseFloat(parts[1]) || 0);
      return parseFloat(ts) || 0;
    };
    // Helper: seconds to MM:SS.s format (preserves decimal precision)
    const toMMSS = (sec: number): string => {
      const mm = Math.floor(sec / 60);
      const ss = sec % 60;
      return `${String(mm).padStart(2, '0')}:${ss < 10 ? '0' : ''}${ss.toFixed(1)}`;
    };
    // Speech estimate: ~2.3 words/sec for pt-BR
    const estimateSpeech = (text: string): number => {
      const words = text.trim().split(/\s+/).filter(Boolean).length;
      return words / 2.3;
    };

    let totalAdded = 0;
    const updated = segments.map((seg) => {
      const startSec = toSec(seg.start) + totalAdded;
      const endSec = toSec(seg.end) + totalAdded;
      const durationSec = Math.max(0.1, endSec - startSec);

      // Use real audio duration if available, else estimate
      const realDuration = segmentAudio.getAudioDuration(seg.index, 'premium', recorder.voiceName);
      const speechSec = realDuration ?? estimateSpeech(seg.text);

      if (speechSec > durationSec * 1.05) {
        // Stretch: add extra time (speech + 0.3s margin)
        const needed = speechSec + 0.3;
        const delta = needed - durationSec;
        totalAdded += delta;
        return {
          ...seg,
          start: toMMSS(startSec),
          end: toMMSS(startSec + needed),
          resolvedAddedSec: (seg.resolvedAddedSec || 0) + delta,
        };
      }

      return {
        ...seg,
        start: toMMSS(startSec),
        end: toMMSS(endSec),
      };
    });

    recorder.setTranscriptionSegments(updated);
    if (recorder.activeVersionIndex === -1) {
      recorder.setOriginalSegments(updated);
    }

    if (totalAdded > 0) {
      toast.success(`Timestamps ajustados: +${totalAdded.toFixed(1)}s adicionados para resolver overflows`);
    } else {
      toast.success('Nenhum overflow detectado.');
    }
  }, [recorder.transcriptionSegments, recorder.setTranscriptionSegments, recorder.setOriginalSegments, recorder.activeVersionIndex, recorder.voiceName, segmentAudio]);

  // ══════════════════════════════════════════════════════
  // ── JSX Render ──
  // ══════════════════════════════════════════════════════
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden', background: '#0f172a', color: '#f8fafc' }}>
      <style>{RECORDER_STYLES}</style>

      {/* Recording FAB — opens in a separate popup window (not captured by screen recording) */}
      <RecordingFAB
        status={recorder.status}
        timer={recorder.timer}
        formatTime={recorder.formatTime}
        recordingSizeBytesRef={recorder.recordingSizeBytesRef}
        onPause={recorder.handlePause}
        onResume={recorder.handleResume}
        onStop={recorder.handleStop}
      />

      {/* Control Toolbar */}
      <ControlToolbar
        onBackToDashboard={handleExit}
        status={recorder.status}
        timer={recorder.timer}
        formatTime={recorder.formatTime}
        addressBarUrl={addressBarUrl}
        setAddressBarUrl={setAddressBarUrl}
        handleNavigateAddressBar={handleNavigateAddressBar}
        handleStart={handleStartRecorder}
        handlePause={recorder.handlePause}
        handleResume={recorder.handleResume}
        handleStop={recorder.handleStop}
        countdownVal={recorder.countdownVal}
        configMenuOpen={configMenuOpen}
        setConfigMenuOpen={setConfigMenuOpen}
        configMenuRef={configMenuRef}
        useMic={recorder.useMic}
        setUseMic={recorder.setUseMic}
        zoomEnabled={zoomEnabled}
        setZoomEnabled={setZoomEnabled}
        spotlightEnabled={snapping.autoSnapEnabled}
        setSpotlightEnabled={snapping.setAutoSnapEnabled}
        keystrokesEnabled={keystrokesEnabled}
        setKeystrokesEnabled={setKeystrokesEnabled}
        forceLogoutOnActivation={forceLogoutOnActivation}
        setForceLogoutOnActivation={setForceLogoutOnActivation}
        onShowHelp={() => {
          setOnboardingStep(0);
          setShowOnboarding(true);
        }}
        isFeedbackMode={isFeedbackMode}
      />

      {/* Frame view */}
      <div style={{ flex: 1, position: 'relative', width: '100%', height: recorder.status === 'recording' ? '100vh' : 'calc(100vh - 56px)', background: '#020617', overflow: 'hidden' }}>

        {/* Floating Shortcuts Indicator (visible when recording/paused/countdown) */}
        {/* Controles Rapidos removed per request */}

        {recorder.errorMsg && (
          <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', background: '#ef4444', color: '#fff', padding: '10px 16px', borderRadius: 8, fontSize: '.78rem', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>⚠️ {recorder.errorMsg}</span>
            <button onClick={() => recorder.setErrorMsg('')} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>X</button>
          </div>
        )}

        {recorder.status === 'initializing' && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 99995, color: '#fff', gap: 16 }}>
            <Loader2 size={48} style={{ color: '#38bdf8', animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>Inicializando Gravador...</div>
            <div style={{ color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center', maxWidth: 400 }}>
              Aguardando permissão de tela e microfone. Confirme as solicitações do seu navegador!
            </div>
          </div>
        )}



        {/* Spotlight Focus Overlay */}
        <SpotlightOverlay
          isSpotlightActive={isSpotlightActive}
          spotlightEnabled={snapping.autoSnapEnabled}
          status={recorder.status}
          customSpotlight={customSpotlight}
          spotlightRadius={spotlightRadius}
          cursorPos={cursorPosRef.current}
          snappedRect={snappedRect}
          isRightDragging={isRightDraggingRef.current}
          hoveredSpotlightId={hoveredSpotlightId}
          lockedSpotlights={lockedSpotlights}
          deletePendingSpotlightId={deletePendingSpotlightId}
          setLockedSpotlights={setLockedSpotlights}
          setDeletePendingSpotlightId={setDeletePendingSpotlightId}
          setDraggedSpotlightId={setDraggedSpotlightId}
          draggedSpotlightId={draggedSpotlightId}
          lastCursorPosRef={lastCursorPosRef}
          cursorPosRef={cursorPosRef}
          hoveredArrowIndex={hoveredArrowIndex}
          setHoveredArrowIndex={setHoveredArrowIndex}
          clickHandledByArrowRef={clickHandledByArrowRef}
          insertIntermediateSpotlight={insertIntermediateSpotlight}
          showCursor={showCursor}
          iframeObstacles={iframeObstaclesRef.current}
        />

        {/* ── Barra de navegação flutuante do iframe (F8 toggle) ── */}
        <IframeNavBar
          iframeRef={iframeRef}
          isRecording={recorder.status === 'recording' || recorder.status === 'paused'}
          addressBarUrl={addressBarUrl}
          setAddressBarUrl={setAddressBarUrl}
          onNavigate={handleNavigateAddressBar}
        />

        <iframe
          id="ecad-simulator-iframe"
          name="ecad-simulator-iframe"
          ref={iframeRef}
          src="about:blank"
          title="ECAD Simulator — Iframe de Simulação"
          onLoad={attachIframeEvents}
          allow="geolocation; microphone; camera; clipboard-write; clipboard-read"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            background: '#ffffff',
            transform: (() => {
              if (isZoomed && zoomEnabled) {
                return `scale(${zoomScale}) translate3d(${zoomTxRef.current}px, ${zoomTyRef.current}px, 0)`;
              }
              return 'scale(1) translate3d(0, 0, 0)';
            })(),
            transition: (isZoomed && zoomEnabled)
              ? (zoomTransitioning ? 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)' : 'none')
              : 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            willChange: 'transform',
            transformOrigin: '50% 50%'
          }}
        />
      </div>

      {/* ── Modal E-CAD Studio ── */}
      <Modal
        open={isLinkingModalOpen}
        onClose={handleDiscardAction}
        title="AI Studio"
        size="wide"
        contentClassName="recorder-modal"
      >
        {/* ── Sidebar vertical ── */}
        <div className="studio-sidebar">
          <div className="studio-sidebar-title">AI Studio</div>

          <button onClick={() => setActiveTab('studio')} className={`studio-tab ${activeTab === 'studio' ? 'active' : ''}`}>
            <span className="studio-tab-number">1</span>
            <Sliders size={13} /> Estúdio
          </button>
          {hasTranscription && (
            <>
              <button onClick={() => setActiveTab('render')} className={`studio-tab ${activeTab === 'render' ? 'active' : ''}`}>
                <span className="studio-tab-number">2</span>
                <Cpu size={13} /> Renderizar
              </button>
              <button onClick={() => setActiveTab('preview')} className={`studio-tab ${activeTab === 'preview' ? 'active' : ''}`}>
                <span className="studio-tab-number">3</span>
                <Eye size={13} /> Preview
              </button>
            </>
          )}
          <button onClick={() => setActiveTab('catalog')} className={`studio-tab ${activeTab === 'catalog' ? 'active' : ''}`}>
            <span className="studio-tab-number">{hasTranscription ? '4' : '2'}</span>
            <LinkIcon size={13} /> Catálogo
          </button>

          <div className="studio-sidebar-spacer" />

          {/* Sidebar footer buttons */}
          <div className="studio-sidebar-footer">
            {(activeTab === 'preview' || activeTab === 'catalog') && (
              <>
                <button
                  onClick={() => handleSaveAction(true)}
                  disabled={recorder.uploading}
                  className="recorder-btn-sidebar"
                  style={{ 
                    border: 'none', background: '#38bdf8', color: '#0f172a', fontWeight: 700,
                    boxShadow: '0 2px 8px rgba(56, 189, 248, 0.25)',
                    opacity: recorder.uploading ? 0.5 : 1
                  }}
                >
                  {recorder.uploading ? <Loader2 size={12} className="animate-spin" /> : <LinkIcon size={12} />}
                  Salvar e Vincular
                </button>
                <button
                  onClick={() => handleSaveAction(false)}
                  disabled={recorder.uploading}
                  className="recorder-btn-sidebar"
                  style={{ 
                    border: '1px solid #334155', background: '#1e293b', color: '#f8fafc',
                    opacity: recorder.uploading ? 0.5 : 1
                  }}
                >
                  {recorder.uploading && <Loader2 size={12} className="animate-spin" />}
                  Salvar Avulso
                </button>
              </>
            )}
            <button
              onClick={handleDiscardAction}
              disabled={recorder.uploading}
              className="recorder-btn-sidebar"
              style={{
                border: '1px solid #334155', background: 'transparent', color: '#94a3b8'
              }}
            >
              <X size={12} /> Cancelar
            </button>
          </div>
        </div>

        {/* ── Content area ── */}
        <div className="studio-content">
          <div className="studio-pane">
            {activeTab === 'studio' && (
              <StudioTab
                previewUrl={recorder.previewUrl}
                legendaUrl={recorder.legendaUrl}
                timer={recorder.timer}
                formatTime={recorder.formatTime}
                videoScript={recorder.videoScript}
                setVideoScript={recorder.setVideoScript}
                transcriptionSegments={recorder.transcriptionSegments}
                isTranscribing={recorder.isTranscribing}
                isRefining={recorder.isRefining}
                videoBlob={recorder.videoBlob}
                handleTranscribe={() => recorder.handleTranscribe(() => setShowManualInput(true)) }
                handleRefine={recorder.handleRefine}
                handleUpdateSegment={recorder.handleUpdateSegment}
                hasTranscription={hasTranscription}
                setShowManualInput={setShowManualInput}
                editingSegmentIndex={editingSegmentIndex}
                setEditingSegmentIndex={setEditingSegmentIndex}
                editingSegmentText={editingSegmentText}
                setEditingSegmentText={setEditingSegmentText}
                refinementVersions={recorder.refinementVersions}
                activeVersionIndex={recorder.activeVersionIndex}
                handleSelectVersion={recorder.handleSelectVersion}
                handleRestoreOriginal={recorder.handleRestoreOriginal}
                playSegment={segmentPlayback.playSegment}
                playSegmentBruto={segmentPlayback.playSegmentBruto}
                stopSegment={segmentPlayback.stopSegment}
                playingSegment={segmentPlayback.playingSegment}
                ttsVoices={ttsPreview.voices}
                ttsSelectedVoiceId={ttsPreview.selectedVoiceId}
                ttsSetSelectedVoiceId={ttsPreview.setSelectedVoiceId}
                ttsGetVoice={stableTtsGetVoice}
                segAudio={segmentAudio}
                timelineEvents={recorder.timelineRef.current}
                healthApi={health}
                onRenderStart={handleRenderStart}
                isRendering={isRendering}
                setVoiceName={recorder.setVoiceName}
                onFixOverflows={handleFixOverflows}
              />
            )}

            {activeTab === 'render' && (
              <RenderTab
                previewUrl={recorder.previewUrl}
                processedVideoUrl={recorder.processedVideoUrl}
                legendaUrl={recorder.legendaUrl}
                transcriptionSegments={recorder.transcriptionSegments}
                voiceName={recorder.voiceName}
                setVoiceName={recorder.setVoiceName}
                modelId={selectedModel}
                setModelId={setSelectedModel}
                isSynthesizing={recorder.isSynthesizing}
                handleSynthesizeAction={handleSynthesizeAction}
                playSegment={segmentPlayback.playSegment}
                stopSegment={segmentPlayback.stopSegment}
                playingSegment={segmentPlayback.playingSegment}
                playVoiceSample={segmentPlayback.playVoiceSample}
                playingSampleVoice={segmentPlayback.playingSampleVoice}
                videoScript={recorder.videoScript}
              />
            )}

            {activeTab === 'preview' && (
              <PreviewTab
                processedVideoUrl={recorder.processedVideoUrl}
                legendaUrl={recorder.legendaUrl}
                voiceName={recorder.voiceName}
                renderedVersions={renderedVersions}
                activeRenderIndex={activeRenderIndex}
                setActiveRenderIndex={setActiveRenderIndex}
              />
            )}

            {activeTab === 'catalog' && (
              <CatalogTab
                videoTitle={videoTitle}
                setVideoTitle={setVideoTitle}
                catalog={catalog}
                loadingCatalog={loadingCatalog}
                selectedPerfilId={selectedPerfilId}
                setSelectedPerfilId={setSelectedPerfilId}
                selectedTopicoId={selectedTopicoId}
                setSelectedTopicoId={setSelectedTopicoId}
                selectedSubtopicoId={selectedSubtopicoId}
                setSelectedSubtopicoId={setSelectedSubtopicoId}
                {...catalogCrud}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Click ripples */}
      {clickRipples.map(ripple => (
        <div
          key={ripple.id}
          className="click-ripple"
          style={{
            position: 'fixed', left: ripple.x, top: ripple.y,
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.4)',
            border: '2px solid rgba(239, 68, 68, 0.8)',
            transform: 'translate(-50%, -50%) scale(0)',
            animation: 'ripple-out 0.4s cubic-bezier(0.1, 0.8, 0.3, 1) forwards',
            pointerEvents: 'none', zIndex: 99998
          }}
        />
      ))}

      {/* Cursor overlay */}
      {/* Cursor overlay — always mounted when recording, positioned via ref */}
      {(recorder.status === 'recording' || recorder.status === 'paused') && (
        <div ref={cursorElRef} style={{ position: 'fixed', left: 0, top: 0, pointerEvents: 'none', zIndex: 99999, transform: 'translate(-2px, -2px)', display: 'none' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.6))' }}>
            <path d="M2 2 L22 6 L9 9 L6 22 Z" fill="white" stroke="white" strokeWidth="2" strokeLinejoin="miter"/>
          </svg>
        </div>
      )}

      {/* Keypress overlay visualizer */}
      <KeypressVisualizer
        showKeys={showKeys}
        keystrokesEnabled={keystrokesEnabled}
        lastKeys={lastKeys}
      />
      {/* Emergency Mode Overlay — CTRL+ALT+DEL-style task manager */}
      <EmergencyOverlay health={health} />

      {/* Feedback Submission Dialog in Recorder */}
      {showFeedbackModalInRecorder && (
        <Modal
          open={showFeedbackModalInRecorder}
          onClose={() => setShowFeedbackModalInRecorder(false)}
          title="Cadastrar Feedback com Gravação"
          size="lg"
          footer={
            <div className="feedback-modal-footer-btns" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', width: '100%' }}>
              <button 
                className="btn-cancel" 
                onClick={() => setShowFeedbackModalInRecorder(false)}
                disabled={iaGenerating}
              >
                Cancelar
              </button>
              <button 
                className="btn-save"
                disabled={iaGenerating || !feedbackTitle.trim()}
                onClick={async () => {
                  setIaGenerating(true);
                  toast.loading('Enviando feedback para a equipe...', { id: 'feedback-submit' });
                  try {
                    let videoPath = recorder.gcsVideoPath || null;
                    if (videoPath) {
                      if (!videoPath.startsWith('http') && !videoPath.startsWith('/api/v1/') && videoPath.startsWith('/ecad/')) {
                        videoPath = videoPath.replace(/^\/ecad\//, '/api/v1/ecad/serve-local/');
                      }
                    }

                    await api.post('/feedback', {
                      tipo: 'PROBLEMA',
                      titulo: feedbackTitle,
                      descricao: feedbackDesc,
                      rota: iframeUrl,
                      prioridade: 'ALTA',
                      screenshot: videoPath
                    });
                    
                    toast.success('Feedback com gravação de vídeo enviado com sucesso para a equipe interna!', { id: 'feedback-submit' });
                    setShowFeedbackModalInRecorder(false);
                    
                    try {
                      if (window.opener && !window.opener.closed) {
                        window.opener.location.href = '/master/feedback';
                      }
                    } catch (e) {
                      console.warn('Erro ao atualizar janela pai:', e);
                    }
                    
                    setTimeout(() => {
                      try {
                        window.close();
                        // Se window.close falhar (como acontece em abas normais nao abertas via script),
                        // redirecionamos a aba atual tbm para garantir
                        setTimeout(() => {
                          window.location.href = '/master/feedback';
                        }, 100);
                      } catch (e) {
                        window.location.href = '/master/feedback';
                      }
                    }, 1200);
                  } catch (err: any) {
                    console.error('Erro ao enviar feedback:', err);
                    toast.error(`Erro ao enviar feedback: ${err.message || 'Falha na conexão'}`, { id: 'feedback-submit' });
                  } finally {
                    setIaGenerating(false);
                  }
                }}
              >
                Enviar Feedback Interno
              </button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Video preview */}
            <div>
              <label className="form-label" style={{ marginBottom: 6, display: 'block' }}>Vídeo Gravado</label>
              {recorder.previewUrl ? (
                <video 
                  src={recorder.previewUrl} 
                  controls 
                  style={{ width: '100%', maxHeight: 240, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)' }} 
                />
              ) : (
                <div style={{ padding: 20, textAlign: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: 12 }}>
                  Nenhum vídeo gravado.
                </div>
              )}
            </div>

            {/* IA Generate Title and Description */}
            <button
              type="button"
              className="btn-save"
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #1e40af 100%)',
                alignSelf: 'flex-start',
                fontSize: '0.76rem',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              disabled={iaGenerating}
              onClick={() => {
                setIaGenerating(true);
                toast.promise(
                  api.post('/feedback/analisar-ia', {
                    text: recorder.videoScript || '',
                    pageUrl: iframeUrl
                  }).then((res) => {
                    if (res.data?.ok) {
                      setFeedbackTitle(res.data.titulo);
                      setFeedbackDesc(res.data.descricao);
                    } else {
                      throw new Error('Falha na análise');
                    }
                    setIaGenerating(false);
                  }).catch((err) => {
                    setIaGenerating(false);
                    throw err;
                  }),
                  {
                    loading: 'Extraindo legenda do vídeo e analisando comportamento com IA...',
                    success: 'Legenda extraída e feedback auto-preenchido com sucesso!',
                    error: 'Erro ao analisar vídeo com IA'
                  }
                );
              }}
            >
              ✨ IA Alice: Analisar Vídeo & Extrair Legenda
            </button>

            {/* Title & description inputs */}
            <div className="form-group">
              <label className="form-label">Título do Feedback *</label>
              <input 
                type="text" 
                className="form-input" 
                placeholder="Ex: Erro ao submeter formulário de cadastro" 
                value={feedbackTitle}
                onChange={e => setFeedbackTitle(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrição e Transcrição</label>
              <textarea 
                className="form-input" 
                rows={5} 
                placeholder="Legendas extraídas e descrição detalhada do problema..." 
                value={feedbackDesc}
                onChange={e => setFeedbackDesc(e.target.value)}
              />
            </div>
          </div>
        </Modal>
      )}

      {publishModalOpen && (
        <Modal
          open={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          title="Publicar Vídeo Online (Produção)"
          size="default"
        >
          <div style={{ padding: '20px', color: '#f8fafc' }}>
            <p style={{ marginBottom: '16px', fontSize: '0.95rem', opacity: 0.9 }}>
              O vídeo foi salvo e vinculado com sucesso à aula local. Deseja publicar esta aula e todos os seus arquivos na Produção agora?
            </p>

            {publishedUrl ? (
              <div style={{ marginTop: '16px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '16px', borderRadius: '8px' }}>
                <p style={{ color: '#10b981', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Rocket size={16} /> Publicado com sucesso na Produção!
                </p>
                <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '12px' }}>
                  Você pode testar e assistir ao vídeo publicado online usando o link abaixo:
                </p>
                <a
                  href={publishedUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: '#10b981',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.background = '#059669')}
                  onMouseOut={(e) => (e.currentTarget.style.background = '#10b981')}
                >
                  <Eye size={15} /> Abrir Vídeo Publicado
                </a>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  onClick={() => setPublishModalOpen(false)}
                  style={{
                    background: 'transparent',
                    color: '#94a3b8',
                    border: '1px solid #334155',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePublishRemote}
                  disabled={publishingRemote}
                  style={{
                    background: '#8b5cf6',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {publishingRemote ? 'Publicando...' : 'Publicar Agora'}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Onboarding Dialog */}
      {showOnboarding && (
        <Modal
          open={showOnboarding}
          onClose={() => {
            if (dontShowOnboardingAgain) {
              localStorage.setItem('ecad_hide_onboarding', 'true');
            }
            setShowOnboarding(false);
          }}
          title=""
          size="wide"
          preventClose={true}
          hideCloseButton={true}
          contentClassName="onboarding-modal-custom"
        >
          <div className="onboarding-container">
            {/* Header Banner */}
            <div className="onboarding-banner">
              <h2 className="onboarding-banner-title">
                🎥 duRecoder Studio
              </h2>
              <p className="onboarding-banner-subtitle">
                Manual Onboarding & Guia de Recursos
              </p>
            </div>

            {/* Modal Body */}
            <div className="onboarding-body">
              
              {/* Step 0: O que é o duRecoder? */}
              {onboardingStep === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 className="onboarding-step-title">
                    1. O que é o duRecoder?
                  </h3>
                  <p className="onboarding-paragraph">
                    O <strong>duRecoder</strong> é um estúdio inteligente de captura de tela acoplado à plataforma Aluno Conectado. 
                    Ele foi projetado para gravar fluxos de uso, criar tutoriais cinematicos e enviar relatórios de bugs e feedbacks de suporte com o máximo de riqueza de detalhes.
                  </p>
                  
                  <div className="onboarding-grid">
                    <div className="onboarding-card">
                      <span className="onboarding-card-title">🎙️ Gravação de Voz Ativa</span>
                      <span className="onboarding-card-desc">
                        Grave suas explicações pelo microfone. O duRecoder captura o áudio e sincroniza com as ações realizadas na tela.
                      </span>
                      {/* Audio visual simulator */}
                      <div className="onboarding-sim-box" style={{ height: 60, marginTop: 8 }}>
                        <div className="voice-sim-wave-container">
                          <div className="voice-sim-bar" />
                          <div className="voice-sim-bar" />
                          <div className="voice-sim-bar" />
                          <div className="voice-sim-bar" />
                          <div className="voice-sim-bar" />
                          <div className="voice-sim-bar" />
                          <div className="voice-sim-bar" />
                        </div>
                      </div>
                    </div>

                    <div className="onboarding-card">
                      <span className="onboarding-card-title">📝 Transcrição Inteligente</span>
                      <span className="onboarding-card-desc">
                        A Inteligência Artificial analisa a gravação de áudio, gera a legenda sincronizada e preenche automaticamente o título e a descrição do seu feedback.
                      </span>
                      <div className="onboarding-sim-box" style={{ height: 60, marginTop: 8, padding: 8, fontSize: '0.75rem', textAlign: 'center', background: '#020617' }}>
                        <span style={{ color: '#38bdf8', fontWeight: 'bold' }}>IA Analisando:</span>
                        <p style={{ margin: '4px 0 0 0', color: '#94a3b8', fontStyle: 'italic', fontSize: '0.68rem' }}>
                          "O sistema gerou automaticamente a legenda com base na sua voz..."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1: Efeitos Visuais & Cinematic (Foco) */}
              {onboardingStep === 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 className="onboarding-step-title">
                    2. Controles Cinematicos e Foco Visual
                  </h3>
                  <p className="onboarding-paragraph">
                    Destaque elementos importantes na tela durante a gravação com efeitos visuais automáticos acionados pelas teclas de atalho:
                  </p>

                  <div className="onboarding-grid">
                    {/* Zoom Card */}
                    <div className="onboarding-card">
                      <span className="onboarding-card-title">🔍 Zoom de Câmera (Segurar SHIFT)</span>
                      <span className="onboarding-card-desc">
                        Mantenha pressionada a tecla <strong>Shift</strong> para aplicar um zoom dinâmico na área ao redor do cursor do mouse, facilitando a leitura de textos pequenos.
                      </span>
                      <div className="onboarding-sim-box">
                        <div className="spotlight-sim-text">
                          PROJETO ALUNO CONECTADO<br />
                          {"[Foco no Cursor]"}
                        </div>
                        <div className="zoom-sim-lens">Zoom</div>
                      </div>
                    </div>

                    {/* Spotlight Card */}
                    <div className="onboarding-card">
                      <span className="onboarding-card-title">💡 Efeito Holofote (Segurar CTRL)</span>
                      <span className="onboarding-card-desc">
                        Mantenha pressionada a tecla <strong>Ctrl</strong> para escurecer o restante da tela e projetar um holofote de luz concentrado sob o cursor do mouse.
                      </span>
                      <div className="onboarding-sim-box">
                        <div className="spotlight-sim-bg" />
                        <div className="spotlight-sim-text">
                          Campo Nome *<br />
                          {"[Digite aqui]"}
                        </div>
                        <div className="spotlight-sim-glow" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Atalhos de Gravação */}
              {onboardingStep === 2 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 className="onboarding-step-title">
                    3. Atalhos Globais do Teclado
                  </h3>
                  <p className="onboarding-paragraph">
                    Controle o duRecoder de forma ágil sem precisar clicar nos botões do estúdio:
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 16 }}>
                    <table className="onboarding-table">
                      <thead>
                        <tr>
                          <th>Atalho</th>
                          <th>Ação / Comando</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><kbd>Ctrl</kbd> + <kbd>F9</kbd></td>
                          <td>Alternar gravação com microfone / silenciosa</td>
                        </tr>
                        <tr>
                          <td><kbd>F10</kbd></td>
                          <td>Pausar ou Retomar a gravação da tela</td>
                        </tr>
                        <tr>
                          <td><kbd>ESC</kbd>{isFeedbackMode && <> ou <kbd>F4</kbd></>}</td>
                          <td>Parar gravação e abrir painel de conclusão</td>
                        </tr>
                        {!isFeedbackMode && (
                          <tr>
                            <td><kbd>F4</kbd></td>
                            <td>Minimizar ou Maximizar o painel flutuante de status</td>
                          </tr>
                        )}
                        <tr>
                          <td><kbd>Ctrl</kbd> + <kbd>PrintScreen</kbd></td>
                          <td>Capturar print screen instantâneo da tela</td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="onboarding-card" style={{ justifyContent: 'center', alignItems: 'center', gap: 12 }}>
                      <span className="onboarding-card-title" style={{ fontSize: '0.85rem' }}>⌨️ Visualizador de Teclas</span>
                      <span className="onboarding-card-desc" style={{ textAlign: 'center' }}>
                        Qualquer tecla pressionada é exibida no rodapé do vídeo gravado para que os espectadores possam acompanhar as ações.
                      </span>
                      <div className="key-sim-key">Ctrl + F9</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Fluxo de Feedback */}
              {onboardingStep === 3 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <h3 className="onboarding-step-title">
                    4. Fluxo Completo de Feedback & Prints
                  </h3>
                  <p className="onboarding-paragraph">
                    Veja como é simples enviar um relatório rico com vídeo, áudio e múltiplas imagens de suporte:
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="onboarding-row">
                      <div className="onboarding-badge">PASSO 1</div>
                      <div className="onboarding-row-text">
                        <strong>Iniciar e Interagir:</strong> Clique em <em>Iniciar Gravação</em> no menu superior do estúdio e execute as tarefas desejadas no simulador da plataforma Aluno Conectado.
                      </div>
                    </div>

                    <div className="onboarding-row">
                      <div className="onboarding-badge">PASSO 2</div>
                      <div className="onboarding-row-text">
                        <strong>Capturar Prints:</strong> Pressione <kbd style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '1px 4px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 'bold' }}>Ctrl + PrintScreen</kbd> a qualquer momento. Miniaturas dos prints aparecerão no lado direito da tela. Clique nelas para ampliá-las.
                      </div>
                    </div>

                    <div className="onboarding-row">
                      <div className="onboarding-badge">PASSO 3</div>
                      <div className="onboarding-row-text">
                        <strong>Concluir Gravação:</strong> Pressione <kbd style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '1px 4px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 'bold' }}>ESC</kbd> ou clique em Parar. O formulário de feedback será aberto, e a IA processará seu áudio para criar o título e a descrição sugeridos automaticamente.
                      </div>
                    </div>

                    <div className="onboarding-row">
                      <div className="onboarding-badge">PASSO 4</div>
                      <div className="onboarding-row-text">
                        <strong>Confirmar e Enviar:</strong> Revise as informações geradas pela IA, anexe os prints adicionais e clique em <em>Enviar Feedback</em> para registrar a ocorrência no painel interno.
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Footer control */}
            <div className="onboarding-footer">
              <label className="onboarding-checkbox-label">
                <input 
                  type="checkbox" 
                  checked={dontShowOnboardingAgain} 
                  onChange={e => setDontShowOnboardingAgain(e.target.checked)}
                  className="onboarding-checkbox"
                />
                Não exibir este guia novamente
              </label>

              <div style={{ display: 'flex', gap: 10 }}>
                {onboardingStep > 0 && (
                  <button 
                    className="onboarding-btn-prev"
                    onClick={() => setOnboardingStep(s => s - 1)}
                  >
                    Anterior
                  </button>
                )}
                {onboardingStep < 3 ? (
                  <button 
                    className="onboarding-btn-next"
                    onClick={() => setOnboardingStep(s => s + 1)}
                  >
                    Próximo
                  </button>
                ) : (
                  <button 
                    className="onboarding-btn-start"
                    onClick={() => {
                      if (dontShowOnboardingAgain) {
                        localStorage.setItem('ecad_hide_onboarding', 'true');
                      }
                      setShowOnboarding(false);
                    }}
                  >
                    Começar
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
